import { Observable, of } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { PaymentMethodCode } from '../types/payment-method.enum';

/**
 * Tap-to-Pay native bridge / deep-link params aligned with order-api webhooks
 * (charge.succeeded metadata: order_id, customer_id, wash_type, package_id, vehicle_id,
 * location_id, admin_user_id, source, paymentMode=terminal).
 *
 * Stripe PI metadata must use the internal DB order id (`orders.id`), not the public display id.
 */
export type TapToPayOrderSource = 'lpr' | 'saas';

export interface BuildTapToPayNativeParamsInput {
  apiOrder?: Record<string, any> | null;
  /** Form / notification order id (may be internal or public until order is loaded). */
  fallbackOrderId?: string;
  source: TapToPayOrderSource;
  amount: string;
  customerId?: string;
  locationId?: string;
  adminUserId?: string;
  washType?: string;
  packageId?: string;
  vehicleId?: string;
  stripeAccountId?: string;
  terminalLocationId?: string;
  phoneNumber?: string;
  email?: string;
  authToken?: string;
  /** Display order number when not on apiOrder (e.g. notification payload). */
  publicOrderId?: string;
}

/** Internal DB id used in Stripe metadata.order_id and order-api webhooks. */
export function resolveInternalOrderId(
  apiOrder: Record<string, any> | null | undefined,
  fallbackOrderId?: string
): string {
  const fromApi = apiOrder?.['id'];
  if (fromApi != null && String(fromApi).trim()) {
    return String(fromApi).replace(/^#+/, '').trim();
  }
  return String(fallbackOrderId ?? '').replace(/^#+/, '').trim();
}

/** Customer-facing order number (display / receipts). */
export function resolvePublicOrderId(
  apiOrder: Record<string, any> | null | undefined,
  explicitPublicOrderId?: string
): string {
  const explicit = explicitPublicOrderId != null ? String(explicitPublicOrderId).replace(/^#+/, '').trim() : '';
  if (explicit) {
    return explicit;
  }
  const candidates = [
    apiOrder?.['order_id'],
    apiOrder?.['orderId'],
    apiOrder?.['public_order_id'],
    apiOrder?.['publicOrderId'],
  ];
  for (const raw of candidates) {
    if (raw != null && String(raw).trim()) {
      return String(raw).replace(/^#+/, '').trim();
    }
  }
  return '';
}

/** Stripe PI metadata keys expected by order-api webhooks for Terminal tap-to-pay. */
export function buildStripeTapToPayMetadata(input: {
  internalOrderId: string;
  publicOrderId: string;
  source: TapToPayOrderSource;
  customerId?: string;
  locationId?: string;
  adminUserId?: string;
  washType?: string;
  packageId?: string;
  vehicleId?: string;
}): Record<string, string> {
  const meta: Record<string, string> = {
    paymentMode: PaymentMethodCode.Terminal,
    source: input.source,
  };
  if (input.internalOrderId) {
    meta['order_id'] = input.internalOrderId;
  }
  if (input.publicOrderId) {
    meta['public_order_id'] = input.publicOrderId;
  }
  if (input.customerId) {
    meta['customer_id'] = input.customerId;
  }
  if (input.locationId) {
    meta['location_id'] = input.locationId;
  }
  const admin = input.adminUserId;
  if (admin) {
    meta['admin_user_id'] = admin;
    meta['user_id'] = admin;
  }
  if (input.washType) {
    meta['wash_type'] = input.washType;
  }
  if (input.packageId) {
    meta['package_id'] = input.packageId;
  }
  if (input.vehicleId) {
    meta['vehicle_id'] = input.vehicleId;
  }
  const wash = (input.washType ?? '').toLowerCase();
  if (wash === 'monthly_membership' || wash.includes('monthly')) {
    // Pay order final_price on Terminal (incl. add-ons), then subscription at package monthly price.
    meta['terminal_flow'] = 'monthly_membership_first_payment';
    meta['tap_to_pay_first_payment'] = 'true';
  }
  return meta;
}

/** Stripe PI metadata for web checkout POS reader (not Android Tap to Pay). */
export function buildTerminalReaderCheckoutMetadata(input: {
  internalOrderId: string;
  publicOrderId: string;
  source: TapToPayOrderSource;
  customerId?: string;
  locationId?: string;
  adminUserId?: string;
  washType?: string;
  packageId?: string;
  vehicleId?: string;
}): Record<string, string> {
  const meta = buildStripeTapToPayMetadata(input);
  meta['terminal_reader_checkout'] = 'true';
  delete meta['tap_to_pay_first_payment'];
  return meta;
}

export function cleanTapToPayAmount(raw: any): string {
  const s = String(raw ?? '0').replace(/[$,]/g, '').trim();
  const n = parseFloat(s) || 0;
  return n.toString();
}

export function cleanTapToPayPhone(raw: any): string {
  return String(raw ?? '').replace(/[\s\-()]/g, '');
}

function setIfPresent(params: URLSearchParams, key: string, value: any): void {
  const v = value != null ? String(value).trim() : '';
  if (v) {
    params.set(key, v);
  }
}

/**
 * Query / JSON fields for {@link TapToPayAndroidNativeService.startPaymentFromParams}.
 */
export function buildTapToPayNativeParams(input: BuildTapToPayNativeParamsInput): URLSearchParams {
  const apiOrder = input.apiOrder ?? null;
  const internalOrderId = resolveInternalOrderId(apiOrder, input.fallbackOrderId);
  const publicOrderId = resolvePublicOrderId(apiOrder, input.publicOrderId);

  const customerId =
    input.customerId ??
    (apiOrder?.['customer_id'] as string | undefined) ??
    (apiOrder?.['customerId'] as string | undefined);
  const locationId =
    input.locationId ??
    (apiOrder?.['location_id'] as string | undefined) ??
    (apiOrder?.['locationId'] as string | undefined);
  const adminUserId =
    input.adminUserId ?? (apiOrder?.['user_id'] as string | undefined);
  const washType = input.washType ?? (apiOrder?.['wash_type'] as string | undefined);
  const packageId =
    input.packageId ?? (apiOrder?.['package_id'] as string | undefined);
  const vehicleId =
    input.vehicleId ?? (apiOrder?.['vehicle_id'] as string | undefined);
  const stripeAccountId =
    input.stripeAccountId ?? (apiOrder?.['stripe_account_id'] as string | undefined);
  const terminalLocationId =
    input.terminalLocationId ?? (apiOrder?.['terminal_location_id'] as string | undefined);

  const params = new URLSearchParams();
  params.set('amount', cleanTapToPayAmount(input.amount));
  params.set('currency', 'USD');
  params.set('source', input.source);
  params.set('paymentMode', PaymentMethodCode.Terminal);

  if (internalOrderId) {
    params.set('orderId', internalOrderId);
    params.set('id', internalOrderId);
  }
  if (publicOrderId) {
    params.set('public_order_id', publicOrderId);
  }

  setIfPresent(params, 'customerId', customerId);
  setIfPresent(params, 'locationId', locationId);
  setIfPresent(params, 'admin_user_id', adminUserId);
  setIfPresent(params, 'wash_type', washType);
  setIfPresent(params, 'package_id', packageId);
  setIfPresent(params, 'vehicle_id', vehicleId);
  setIfPresent(params, 'stripe_account_id', stripeAccountId);
  setIfPresent(params, 'terminal_location_id', terminalLocationId);
  setIfPresent(params, 'phoneNumber', cleanTapToPayPhone(input.phoneNumber));
  setIfPresent(params, 'email', input.email);
  if (input.authToken) {
    params.set('token', input.authToken);
  }

  const stripeMetadata = buildStripeTapToPayMetadata({
    internalOrderId,
    publicOrderId,
    source: input.source,
    customerId: customerId != null ? String(customerId).trim() : undefined,
    locationId: locationId != null ? String(locationId).trim() : undefined,
    adminUserId: adminUserId != null ? String(adminUserId).trim() : undefined,
    washType: washType != null ? String(washType).trim() : undefined,
    packageId: packageId != null ? String(packageId).trim() : undefined,
    vehicleId: vehicleId != null ? String(vehicleId).trim() : undefined,
  });
  params.set('stripe_metadata', JSON.stringify(stripeMetadata));

  return params;
}

/** Match Pusher / Android callback order id to internal or public ids we track. */
export function tapToPayOrderIdsMatch(
  eventOrderId: string,
  trackedIds: (string | null | undefined)[]
): boolean {
  const incoming = String(eventOrderId ?? '').replace(/^#+/, '').trim();
  if (!incoming) {
    return false;
  }
  const normalized = new Set<string>();
  for (const id of trackedIds) {
    const t = String(id ?? '').replace(/^#+/, '').trim();
    if (t) {
      normalized.add(t);
    }
  }
  return normalized.has(incoming);
}

/**
 * After Stripe Terminal tap-to-pay succeeds, tell the backend to record `card_present`
 * and move the order to scheduled (same as cash / membership confirm-payment-mode).
 */
export function confirmTapToPayPaymentMode(
  apiService: ApiService,
  orderId: string
): Observable<any> {
  const internalId = resolveInternalOrderId(null, orderId);
  if (!internalId) {
    return of(null);
  }
  return apiService.post(`order/confirm-payment-mode/${internalId}`, {
    payment_mode: PaymentMethodCode.CardPresent,
  });
}